/* eslint-disable */
import { logger } from "@utilities/logger";

export type RecordingInfoType = {
  isRecording: boolean;
  normalizedLoudness: null | number; // Loudness between 0 and 1
  recordedAudioDurationSecs: null | number;
};

export class AudioRecorder {
  _analyser: AnalyserNode | null;
  _analyserBuffer: Float32Array<ArrayBuffer> | null;
  _getDuration = (): number => {
    if (this._startedRecordingAtUnixMs === null) {
      return 0;
    }
    return (Date.now() - this._startedRecordingAtUnixMs) / 1000;
  };
  _getPeakLoudness = (): number => {
    if (this._analyser === null || this._analyserBuffer === null) {
      return 0;
    }
    this._analyser.getFloatTimeDomainData(this._analyserBuffer);
    let squaredSum = 0.0;
    for (let i = 0; i < this._analyserBuffer.length; i++) {
      const value = this._analyserBuffer[i];
      squaredSum += Math.pow(value, 2);
    }
    return Math.sqrt(squaredSum / this._analyserBuffer.length);
  };
  _loudnessEma: number;
  _mediaRecorder: MediaRecorder | null;
  _pollAudioProgress = async () => {
    while (this.recordingInfo.isRecording && this.recordingInfo.normalizedLoudness !== null) {
      const loudness = Math.min(this._getPeakLoudness() * 10.0, 1.0);
      const duration = this._getDuration();
      this.recordingInfo = {
        ...this.recordingInfo,
        normalizedLoudness:
          this._loudnessEma * this.recordingInfo.normalizedLoudness + (1 - this._loudnessEma) * loudness,
        recordedAudioDurationSecs: duration,
      };
      this.onUpdate?.(this.recordingInfo);
      if (this.maxDurationSecs !== null && duration >= this.maxDurationSecs) {
        this.stopRecording();
        if (this.onMaxDurationCb !== null) {
          this.onMaxDurationCb();
        }
        break;
      } else {
        await new Promise(r => setTimeout(r, 500));
      }
    }
  };
  _recordedChunks: Blob[];
  _startedRecordingAtUnixMs: null | number;
  _stream: MediaStream | null;
  getBlob = () => {
    const blob = new Blob(this._recordedChunks, {
      type: "audio/ogg; codecs=opus",
    });
    return blob;
  };
  getExtension = () => {
    const mimeType = this.getMimeType();
    if (mimeType === undefined) {
      return null;
    }
    const extension = mimeType.split(";")[0].split("/")[1];
    return extension;
  };
  getMimeType = () => {
    return this._mediaRecorder?.mimeType;
  };
  maxDurationSecs: null | number;
  onDataAvailable: ((blob: Blob) => any) | null;

  onMaxDurationCb: (() => any) | null;

  onStart: (() => any) | null;

  onStop: (() => any) | null;

  onUpdate: ((recordingInfo: RecordingInfoType) => any) | null;

  onError: ((error: Error) => any) | null;

  options: { mimeType?: string };

  recordingInfo: RecordingInfoType;

  reset = () => {
    this._mediaRecorder = null;
    this._recordedChunks = [];
    this._stream = null;
    this._analyser = null;
    this._analyserBuffer = null;
    this.recordingInfo = {
      isRecording: false,
      normalizedLoudness: null,
      recordedAudioDurationSecs: null,
    };
    this._startedRecordingAtUnixMs = null;
  };

  startRecording = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(async (stream: MediaStream) => {
        this._recordedChunks = [];
        this._stream = stream;
        this._mediaRecorder =
          typeof MediaRecorder === "undefined" ? null : new MediaRecorder(this._stream, this.options);

        const context = new AudioContext();
        const source = context.createMediaStreamSource(stream);

        this._analyser = context.createAnalyser();
        this._analyser.fftSize = 512;
        source.connect(this._analyser);

        this._analyserBuffer = new Float32Array(this._analyser.fftSize);

        if (this._mediaRecorder !== null) {
          this._mediaRecorder.ondataavailable = e => {
            this._recordedChunks.push(e.data);
            if (this.onDataAvailable) {
              this.onDataAvailable(e.data);
            }
          };
          this._mediaRecorder.onstart = () => {
            this._startedRecordingAtUnixMs = Date.now();
            if (this.onStart !== null) {
              this.onStart();
            }
          };
          this._mediaRecorder.onstop = this.onStop;
          this._mediaRecorder.start(100);
        }

        this.recordingInfo = {
          isRecording: true,
          normalizedLoudness: 0,
          recordedAudioDurationSecs: 0,
        };

        this._pollAudioProgress();
      })
      .catch(error => {
        logger.error(error instanceof Error ? error : null, "Error starting recording");
        if (this.onError) {
          this.onError(error);
        }
      });
  };

  stopRecording = (onError: ((error: any) => any) | undefined = undefined) => {
    try {
      if (this._mediaRecorder && this._mediaRecorder.state === "recording" && this._stream) {
        this._mediaRecorder.stop();
        this._stream.getTracks().forEach(track => {
          track.stop();
        });
      }
    } catch (error: any) {
      if (onError) {
        onError(error);
      }
    }
    this.recordingInfo = {
      ...this.recordingInfo,
      isRecording: false,
    };
    this.onUpdate?.(this.recordingInfo);
    this._startedRecordingAtUnixMs = null;
  };

  constructor({
    maxDurationSecs = null,
    onMaxDurationCb = null,
    onUpdate = null,
  }: {
    maxDurationSecs?: null | number;
    onMaxDurationCb?: (() => any) | null;
    onUpdate?: ((recordingInfo: RecordingInfoType) => any) | null;
  }) {
    this._mediaRecorder = null;
    this._stream = null;
    this._recordedChunks = [];
    this.onDataAvailable = null;
    this.onStop = null;
    this.onStart = null;
    this.onUpdate = onUpdate;
    this.onError = null;
    this.options = {};
    this._analyserBuffer = null;
    this._analyser = null;
    this._startedRecordingAtUnixMs = null;
    for (const mimeType of ["audio/webm", "audio/mp4"]) {
      if (
        typeof "mediaRecorder" !== "undefined" &&
        typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported(mimeType)
      ) {
        this.options = { mimeType };
      }
    }
    this.recordingInfo = {
      isRecording: false,
      normalizedLoudness: null,
      recordedAudioDurationSecs: null,
    };
    this._loudnessEma = 0.5;
    this.maxDurationSecs = maxDurationSecs;
    this.onMaxDurationCb = onMaxDurationCb;
  }
}
